import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useCancelPlanContext } from "./cancel-provider";
import { format } from "date-fns";
import { getPriceForThePlan } from "@/lib/plan.utils";
import { PaymentInterval } from "@/types/subscription.types";
import { Mail, Calendar, DollarSign } from "lucide-react";

const CancelReviewPlan = () => {
  const { state, planDetails, subscription, searchParams, handleStateChange } =
    useCancelPlanContext();

  if (!planDetails || !subscription) {
    return <div>No plan details or subscription found</div>;
  }

  const renderPriceBasedOnInterval = () => {
    const price =
      getPriceForThePlan({
        planType: planDetails?.planId?.planType,
        planDetails: planDetails,
        subscription: subscription,
      }) || 0;
    const totalPrice = price * searchParams.quantity;

    if (subscription.interval === PaymentInterval.ANNUAL) {
      return (totalPrice * 12).toFixed(2);
    }

    if (subscription.interval === PaymentInterval.QUARTERLY) {
      return (totalPrice * 3).toFixed(2);
    }

    return totalPrice.toFixed(2);
  };

  const handleContinue = () => {
    handleStateChange({ step: 2 });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review Your Plan</CardTitle>
        <CardDescription>
          Please review your current subscription details before proceeding
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 dark:bg-blue-900/20 p-2 rounded-full">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Plan Details</h3>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>
                    <span className="font-medium">Plan Name:</span>{" "}
                    {planDetails.planId?.planName ||
                      planDetails.addonPlanId?.name ||
                      "N/A"}
                  </p>
                  <p>
                    <span className="font-medium">Quantity:</span>{" "}
                    {searchParams.quantity}
                  </p>
                  <p>
                    <span className="font-medium">Warmup Emails:</span>{" "}
                    {planDetails.maxWarmupEmailsDaily} per day /{" "}
                    {planDetails.maxWarmupEmailsMonthly} per month
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-4">
              <div className="bg-green-100 dark:bg-green-900/20 p-2 rounded-full">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Billing Information</h3>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>
                    <span className="font-medium">Billing Interval:</span>{" "}
                    {subscription.interval}
                  </p>
                  <p>
                    <span className="font-medium">Current Price:</span> $
                    {renderPriceBasedOnInterval()}/
                    {subscription.interval.toLowerCase()}
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-4">
              <div className="bg-amber-100 dark:bg-amber-900/20 p-2 rounded-full">
                <Calendar className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Subscription Status</h3>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>
                    <span className="font-medium">Status:</span>{" "}
                    {subscription.status}
                  </p>
                  <p>
                    <span className="font-medium">Active Until:</span>{" "}
                    {format(subscription.endDate, "MMM d, yyyy")}
                  </p>
                  <p className="text-amber-600 dark:text-amber-400 mt-2">
                    Your subscription will remain active until the end of your
                    current billing cycle. After cancellation, you'll lose
                    access to all plan features.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button onClick={handleContinue}>Continue</Button>
      </CardFooter>
    </Card>
  );
};

export default CancelReviewPlan;
